import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Copy,
  FileDown,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { useComparison } from "@/hooks/useComparison";
import { useComparisonStore } from "@/stores/comparison-store";
import { comparisonToMarkdown } from "@/lib/copyComparisonMarkdown";
import { trackEvent } from "@/lib/engagement";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import { UpgradeCard } from "@/components/UpgradeCard";

const PRESET_CHIPS = [
  "Focus",
  "Method",
  "Findings",
  "Limitations",
  "Contribution",
  "Sample",
  "Outcome",
  "Theoretical framework",
] as const;

const DEFAULT_ENABLED = ["Method", "Findings", "Limitations"];

interface Props {
  conceptId: string;
}

export function ComparisonTable({ conceptId }: Props) {
  const { data: currentUser } = useCurrentUser();
  const paid = isPaidPlan(currentUser?.plan);
  const { data: cached, isLoading, generate, clear } = useComparison(conceptId);

  const bucket = useComparisonStore((s) => s.byConcept[conceptId]);
  const togglePaper = useComparisonStore((s) => s.togglePaper);
  const toggleColumn = useComparisonStore((s) => s.toggleColumn);
  const addCustomColumn = useComparisonStore((s) => s.addCustomColumn);
  const removeCustomColumn = useComparisonStore((s) => s.removeCustomColumn);
  const requestInsert = useComparisonStore((s) => s.requestInsert);

  const selectedPaperIds = bucket?.selectedPaperIds ?? new Set<string>();
  const selectedPresetColumns =
    bucket?.selectedPresetColumns ?? new Set<string>();
  const customColumns = bucket?.customColumns ?? [];

  // Pre-enable Method/Findings/Limitations on first concept-mount.
  useMemo(() => {
    if (!bucket && DEFAULT_ENABLED.length > 0) {
      DEFAULT_ENABLED.forEach((c) => toggleColumn(conceptId, c));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptId]);

  const [editMode, setEditMode] = useState(false);
  const [customDraft, setCustomDraft] = useState("");

  const inResultMode = !!cached && !editMode;
  const selectionCount = selectedPaperIds.size;
  const totalColumns = selectedPresetColumns.size + customColumns.length;

  const canRun = selectionCount >= 2 && selectionCount <= 7 && totalColumns > 0;

  const handleRun = () => {
    if (!canRun) return;
    trackEvent("comparison_run", {
      paper_count: selectionCount,
      preset_count: selectedPresetColumns.size,
      custom_count: customColumns.length,
    });
    generate.mutate(
      {
        paper_ids: Array.from(selectedPaperIds),
        columns: Array.from(selectedPresetColumns),
        custom_columns: customColumns,
      },
      {
        onSuccess: () => setEditMode(false),
        onError: (err) => {
          toast.error("Comparison failed", {
            description: err instanceof Error ? err.message : "Unknown error",
          });
        },
      },
    );
  };

  const handleAddCustom = () => {
    if (!customDraft.trim()) return;
    addCustomColumn(conceptId, customDraft);
    setCustomDraft("");
  };

  const handleCopy = async () => {
    if (!cached) return;
    await navigator.clipboard.writeText(comparisonToMarkdown(cached));
    toast.success("Copied to clipboard");
  };

  const handleInsert = () => {
    if (!cached) return;
    requestInsert(conceptId, cached);
    toast.success("Open Writing to see the insert", {
      description:
        "Switch to Evidence → Write to find the table at the end of your draft.",
    });
  };

  const handleClear = () => {
    clear.mutate(undefined, {
      onSuccess: () => setEditMode(false),
    });
  };

  void togglePaper;

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  if (currentUser && !paid) {
    return (
      <UpgradeCard
        feature="Comparison"
        description="Compare papers head-to-head with AI across the dimensions you choose."
      />
    );
  }

  if (inResultMode && cached) {
    const cols = [...cached.columns, ...cached.custom_columns];
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-1 border-b px-3 py-2">
          <Button size="sm" variant="ghost" onClick={() => setEditMode(true)}>
            <ArrowLeft className="size-3.5 mr-1" /> Edit selection
          </Button>
          <div className="ml-auto flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={handleCopy}>
              <Copy className="size-3.5 mr-1" /> Copy markdown
            </Button>
            <Button size="sm" variant="ghost" onClick={handleInsert}>
              <FileDown className="size-3.5 mr-1" /> Insert in draft
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleRun}
              disabled={generate.isPending}
            >
              <RefreshCw
                className={`size-3.5 mr-1 ${generate.isPending ? "animate-spin" : ""}`}
              />
              Rerun
            </Button>
            <Button size="sm" variant="ghost" onClick={handleClear}>
              <Trash2 className="size-3.5 mr-1" /> Clear
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {cached.synthesis && (
            <div className="rounded-md bg-muted p-3 text-sm">
              {cached.synthesis}
            </div>
          )}
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b">
                <th className="px-2 py-1.5 text-left font-medium text-muted-foreground">
                  Paper
                </th>
                {cols.map((c) => (
                  <th
                    key={c}
                    className="px-2 py-1.5 text-left font-medium text-muted-foreground"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cached.rows.map((r) => (
                <tr
                  key={r.paper_id}
                  className="border-b last:border-0 align-top"
                >
                  <td className="px-2 py-1.5 text-xs">
                    <div className="font-medium">{r.title}</div>
                    <div className="text-muted-foreground">
                      {r.authors[0] ?? "Unknown"}
                      {r.year != null ? ` · ${r.year}` : ""}
                    </div>
                  </td>
                  {cols.map((c) => (
                    <td key={c} className="px-2 py-1.5">
                      {r.columns[c] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-3 py-2 space-y-2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Compare on
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PRESET_CHIPS.map((c) => {
            const active = selectedPresetColumns.has(c);
            return (
              <button
                key={c}
                type="button"
                role="button"
                aria-label={c}
                data-active={active ? "true" : "false"}
                onClick={() => toggleColumn(conceptId, c)}
                className={`rounded-full px-2.5 py-0.5 text-xs ${
                  active
                    ? "bg-indigo-500/30 text-foreground"
                    : "border opacity-60 hover:opacity-100"
                }`}
              >
                {active ? "✓ " : ""}
                {c}
              </button>
            );
          })}
          {customColumns.map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-500/25 px-2.5 py-0.5 text-xs"
            >
              {c}
              <button
                type="button"
                aria-label={`Remove ${c}`}
                onClick={() => removeCustomColumn(conceptId, c)}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={customDraft}
            onChange={(e) => setCustomDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddCustom();
            }}
            placeholder="+ Add custom column"
            className="h-7 w-48 text-xs"
          />
          <Button size="sm" variant="ghost" onClick={handleAddCustom}>
            <Plus className="size-3.5" /> Add
          </Button>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center gap-3 p-4 text-center">
        {generate.isPending ? (
          <>
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Comparing {selectionCount} papers…
            </p>
          </>
        ) : (
          <>
            <Sparkles className="size-6 text-muted-foreground opacity-50" />
            <p className="text-sm text-muted-foreground">
              Pick 2–7 papers from the list, then run.
            </p>
            <p className="text-xs text-muted-foreground">
              {selectionCount} selected · {totalColumns} column
              {totalColumns === 1 ? "" : "s"}
            </p>
            <Button size="sm" onClick={handleRun} disabled={!canRun}>
              {`Run on ${selectionCount} paper${selectionCount === 1 ? "" : "s"}`}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
